/**
 * Zod validation schemas for Space request payloads.
 *
 * Shared between the client (create/edit flows) and the API routes so slug,
 * name, visibility, and cipher-challenge rules stay in a single place.
 *
 * @module schemas/spaces
 */
import { z } from 'zod';
import { isValidReactionEmoji, REACTION_EMOJI_MAX_LENGTH } from '../reaction-emoji';
import { SPACE_VISIBILITY_VALUES, SPACE_CHANNEL_TYPES, SPACE_SLUG_MIN_LENGTH, SPACE_SLUG_MAX_LENGTH, SPACE_SLUG_PATTERN, SPACE_NAME_MIN_LENGTH, SPACE_NAME_MAX_LENGTH, SPACE_DESCRIPTION_MAX_LENGTH, SPACE_ROLE_DESCRIPTION_MAX_LENGTH, SPACE_CHANNEL_NAME_MIN_LENGTH, SPACE_CHANNEL_NAME_MAX_LENGTH, SPACE_MESSAGE_MAX_LENGTH, SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH, SPACE_MESSAGE_MAX_EXPIRES_SECONDS, SPACE_MESSAGE_MAX_ATTACHMENTS, SPACE_SEED_ROLE_SYSTEMS, SPACE_PERMISSIONS, SPACE_REACTION_TAG_HEX_LENGTH, SPACE_IDENTITY_BLOB_CIPHERTEXT_MAX_LENGTH, } from '../api/spaces-types';
import { SPACE_ANNOUNCEMENT_AUDIENCE_MATCHES, SPACE_ANNOUNCEMENT_AUDIENCE_MAX_CHANNELS, SPACE_ANNOUNCEMENT_AUDIENCE_MAX_ROLES, SPACE_ANNOUNCEMENT_CIPHERTEXT_MAX_LENGTH, SPACE_ANNOUNCEMENT_CONTENT_MAX_LENGTH, SPACE_ANNOUNCEMENT_MAX_ATTACHMENTS, SPACE_ANNOUNCEMENT_PAGE_DEFAULT, SPACE_ANNOUNCEMENT_PAGE_MAX, normalizeAnnouncementAudience, } from '../api/space-announcements';
import { SPACE_NOTIFY_LEVELS, SPACE_NOTIFICATION_OVERRIDES_MAX_ENTRIES, } from '../api/space-notifications';
import { SPACE_ACTIVITY_SORTS, SPACE_ACTIVITY_TOP_WINDOWS, SPACE_ACTIVITY_WEIGHT_MAX, } from '../api/space-activity';
import { SPACE_THEME_TOKEN_KEYS } from '../types/space-theme';
import { HEX_COLOR_PATTERN } from '../utils/color';
import { SPACE_INVITE_CODE_PATTERN, SPACE_INVITE_EXPIRY_OPTIONS, SPACE_INVITE_MAX_USES_LIMIT, SPACE_TEMPORARY_ACCESS_DURATIONS, } from '../constants/space-invite-rules';
export const SpaceVisibilitySchema = z.enum(SPACE_VISIBILITY_VALUES);
// ---------------------------------------------------------------------------
// Charset guards for opaque cipher fields (blind relay).
//
// The server never decrypts these, but their encodings are fixed by
// @adieuu/crypto: binary fields are standard base64 (`serializeCipherPayload`,
// `createCipherCheck`), `knownValue` is base64url (`generateKnownValue`), and
// cipher ids are hex digests (`generateCipherId`). A cheap charset check stops
// arbitrary text/JSON being smuggled through fields that bypass sanitization.
// ---------------------------------------------------------------------------
const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;
const HEX_PATTERN = /^[0-9a-fA-F]+$/;
const base64Field = (max) => z.string().min(1).max(max).regex(BASE64_PATTERN, 'must be base64');
const CipherIdSchema = z
    .string()
    .min(1)
    .max(256)
    .regex(HEX_PATTERN, 'cipherId must be a hex digest');
export const SpaceSlugSchema = z
    .string()
    .min(SPACE_SLUG_MIN_LENGTH)
    .max(SPACE_SLUG_MAX_LENGTH)
    .regex(SPACE_SLUG_PATTERN, 'Slug must be lowercase letters, numbers, and internal hyphens');
const KnownValueSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(BASE64URL_PATTERN, 'must be base64url');
/** Blind-relay cipher verification challenge (opaque to the server). */
export const CipherCheckSchema = z.object({
    knownValue: KnownValueSchema,
    encryptedKnownValue: base64Field(500),
    nonce: base64Field(100),
});
/** Decrypted challenge plaintext for `cipherRequired` join / invite accept. */
export const SpaceCipherJoinProofSchema = z.object({
    knownValue: KnownValueSchema.optional(),
});
const EncryptedSpaceFieldSchema = z.object({
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH),
    nameNonce: base64Field(500),
    cipherId: CipherIdSchema,
});
export const CreateSpaceEncryptedSeedSchema = z.object({
    category: EncryptedSpaceFieldSchema,
    channel: EncryptedSpaceFieldSchema,
    roles: z
        .array(EncryptedSpaceFieldSchema.extend({
        system: z.enum(SPACE_SEED_ROLE_SYSTEMS),
    }))
        .length(2)
        .refine((roles) => {
        const systems = new Set(roles.map((r) => r.system));
        return systems.has('admin') && systems.has('everyone');
    }, { message: 'encryptedSeed.roles must include admin and everyone' }),
});
export const CreateSpaceSchema = z
    .object({
    id: z.string().length(24).optional(),
    slug: SpaceSlugSchema.optional(),
    name: z.string().min(SPACE_NAME_MIN_LENGTH).max(SPACE_NAME_MAX_LENGTH).optional(),
    description: z.string().max(SPACE_DESCRIPTION_MAX_LENGTH).optional(),
    visibility: SpaceVisibilitySchema,
    allowFreeMembers: z.boolean().optional(),
    cipherCheck: CipherCheckSchema.optional(),
    e2ee: z.boolean().optional(),
    encryptIdentity: z.boolean().optional(),
    cipherRequired: z.boolean().optional(),
    encryptedSeed: CreateSpaceEncryptedSeedSchema.optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    cipherId: CipherIdSchema.optional(),
    encryptedDescription: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    descriptionNonce: base64Field(500).optional(),
})
    .refine((v) => !(v.visibility === 'public' &&
    (v.cipherCheck || v.e2ee || v.cipherRequired || v.encryptIdentity)), {
    message: 'Public spaces cannot use Cipher gates or E2EE',
    path: ['cipherCheck'],
})
    .refine((v) => !((v.e2ee || v.cipherRequired) && !v.cipherCheck), {
    message: 'cipherCheck is required when e2ee or cipherRequired is enabled',
    path: ['cipherCheck'],
})
    .refine((v) => !(v.encryptIdentity && !v.e2ee), {
    message: 'Encrypting the Space name requires end-to-end encryption',
    path: ['encryptIdentity'],
})
    .refine((v) => !(v.e2ee && !v.encryptedSeed), {
    message: 'encryptedSeed is required when e2ee is enabled',
    path: ['encryptedSeed'],
})
    .refine((v) => {
    if (!v.encryptIdentity)
        return true;
    return !!(v.encryptedName && v.nameNonce && v.cipherId);
}, {
    message: 'Encrypted name fields are required when the Space name is encrypted',
    path: ['encryptedName'],
})
    .refine((v) => {
    if (!v.encryptIdentity)
        return true;
    // No plaintext identity when encrypting for the directory.
    return !v.name && !v.description;
}, {
    message: 'A plaintext name or description cannot be set when the Space name is encrypted',
    path: ['name'],
})
    .refine((v) => {
    if (v.encryptIdentity)
        return true;
    return typeof v.name === 'string' && v.name.length >= SPACE_NAME_MIN_LENGTH;
}, {
    message: 'A Space name is required when the Space name is not encrypted',
    path: ['name'],
})
    .refine((v) => {
    const hasDesc = !!v.encryptedDescription;
    const hasNonce = !!v.descriptionNonce;
    return hasDesc === hasNonce;
}, {
    message: 'encryptedDescription and descriptionNonce must be provided together',
    path: ['encryptedDescription'],
})
    .refine((v) => !(v.encryptedDescription && !v.encryptIdentity), {
    message: 'An encrypted description requires the Space name to be encrypted',
    path: ['encryptedDescription'],
})
    // Public/unlisted Spaces need a custom vanity slug; Hidden Spaces use ObjectId.
    .refine((v) => {
    if (v.visibility === 'hidden')
        return true;
    return typeof v.slug === 'string' && v.slug.length >= SPACE_SLUG_MIN_LENGTH;
}, {
    message: 'slug is required for public and unlisted spaces',
    path: ['slug'],
})
    .refine((v) => {
    if (v.visibility !== 'hidden')
        return true;
    return typeof v.id === 'string' && v.id.length === 24;
}, {
    message: 'id is required for hidden spaces (used as the routing slug)',
    path: ['id'],
})
    .refine((v) => {
    if (v.visibility !== 'hidden' || !v.id)
        return true;
    // Client may omit slug (server assigns id) or must send slug === id.
    return v.slug === undefined || v.slug === v.id;
}, {
    message: 'hidden space slug must equal id when provided',
    path: ['slug'],
});
const spaceThemeHex = z.string().regex(HEX_COLOR_PATTERN, 'must be a #RRGGBB hex colour');
/** Strict-hex Space theme. Unknown keys are rejected so nothing else reaches inline styles. */
export const SpaceThemeSchema = z
    .object(Object.fromEntries(SPACE_THEME_TOKEN_KEYS.map((key) => [key, spaceThemeHex.optional()])))
    .strict();
const MediaIdSchema = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9_-]+$/, 'must be a media id');
const identityBlobField = base64Field(SPACE_IDENTITY_BLOB_CIPHERTEXT_MAX_LENGTH);
/** Encrypted field → nonce (and, for media, the E2E blob id) that must travel together. */
const ENCRYPTED_UPDATE_GROUPS = [
    ['encryptedName', 'nameNonce'],
    ['encryptedDescription', 'descriptionNonce'],
    ['encryptedLogo', 'logoNonce', 'logoE2eMediaId'],
    ['encryptedBanner', 'bannerNonce', 'bannerE2eMediaId'],
    ['encryptedTheme', 'themeNonce'],
];
/** Fields that edit the Space profile (gated by `editProfile`). */
export const UPDATE_SPACE_PROFILE_FIELDS = [
    'name',
    'description',
    'logoMediaId',
    'bannerMediaId',
    'removeLogo',
    'removeBanner',
    'encryptedName',
    'nameNonce',
    'encryptedDescription',
    'descriptionNonce',
    'encryptedLogo',
    'logoNonce',
    'logoE2eMediaId',
    'encryptedBanner',
    'bannerNonce',
    'bannerE2eMediaId',
];
/** Fields that edit the Space theme (gated by `editTheme`). */
export const UPDATE_SPACE_THEME_FIELDS = ['theme', 'encryptedTheme', 'themeNonce', 'clearTheme'];
/** Fields that change Space settings (gated by `manageMetadata`). */
export const UPDATE_SPACE_SETTINGS_FIELDS = ['visibility', 'allowFreeMembers', 'cipherRequired'];
export const UpdateSpaceSchema = z
    .object({
    // profile
    name: z.string().min(SPACE_NAME_MIN_LENGTH).max(SPACE_NAME_MAX_LENGTH).optional(),
    description: z.string().max(SPACE_DESCRIPTION_MAX_LENGTH).optional(),
    logoMediaId: MediaIdSchema.optional(),
    bannerMediaId: MediaIdSchema.optional(),
    removeLogo: z.boolean().optional(),
    removeBanner: z.boolean().optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    encryptedDescription: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    descriptionNonce: base64Field(500).optional(),
    encryptedLogo: identityBlobField.optional(),
    logoNonce: base64Field(500).optional(),
    logoE2eMediaId: MediaIdSchema.optional(),
    encryptedBanner: identityBlobField.optional(),
    bannerNonce: base64Field(500).optional(),
    bannerE2eMediaId: MediaIdSchema.optional(),
    cipherId: CipherIdSchema.optional(),
    // theme
    theme: SpaceThemeSchema.optional(),
    encryptedTheme: identityBlobField.optional(),
    themeNonce: base64Field(500).optional(),
    clearTheme: z.boolean().optional(),
    // settings
    visibility: SpaceVisibilitySchema.optional(),
    allowFreeMembers: z.boolean().optional(),
    cipherRequired: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field is required',
})
    .refine((v) => {
    const record = v;
    return ENCRYPTED_UPDATE_GROUPS.every((group) => {
        const present = group.filter((key) => record[key] !== undefined).length;
        return present === 0 || present === group.length;
    });
}, { message: 'Encrypted fields must be sent together with their nonce (and media id)' })
    .refine((v) => {
    const record = v;
    const hasEncrypted = ENCRYPTED_UPDATE_GROUPS.some((group) => record[group[0]] !== undefined);
    return hasEncrypted === (v.cipherId !== undefined);
}, { message: 'cipherId is required with encrypted fields and only allowed with them', path: ['cipherId'] })
    .refine((v) => !(v.name !== undefined && v.encryptedName !== undefined), {
    message: 'Send either a plaintext or an encrypted name, not both',
    path: ['name'],
})
    .refine((v) => !(v.description !== undefined && v.encryptedDescription !== undefined), {
    message: 'Send either a plaintext or an encrypted description, not both',
    path: ['description'],
})
    .refine((v) => [v.logoMediaId, v.encryptedLogo, v.removeLogo ? true : undefined].filter((x) => x !== undefined).length <= 1, { message: 'Set, encrypt, or remove the logo — only one', path: ['removeLogo'] })
    .refine((v) => [v.bannerMediaId, v.encryptedBanner, v.removeBanner ? true : undefined].filter((x) => x !== undefined).length <=
    1, { message: 'Set, encrypt, or remove the banner — only one', path: ['removeBanner'] })
    .refine((v) => [v.theme, v.encryptedTheme, v.clearTheme ? true : undefined].filter((x) => x !== undefined).length <= 1, { message: 'Set, encrypt, or clear the theme — only one', path: ['clearTheme'] });
/**
 * Time-limited membership granted by an invite. `until_event` is a reserved
 * kind (see `SPACE_TEMPORARY_ACCESS_KINDS`) and is rejected until event-driven
 * expiry exists, so a client cannot store an expiry that nothing enforces.
 */
export const SpaceTemporaryAccessSchema = z.object({
    kind: z.literal('duration'),
    duration: z.enum(SPACE_TEMPORARY_ACCESS_DURATIONS),
});
/** Roles applied on top of the default Member role when an invite is accepted. */
const GrantedRoleIdsSchema = z.array(z.string().length(24)).max(20);
/** Channels an invite may bind via a generated role; server re-checks view + ACL. */
const GrantedChannelIdsSchema = z.array(z.string().length(24)).max(50);
export const CreateSpaceInviteSchema = z.object({
    identityId: z.string().length(24),
    grantedRoleIds: GrantedRoleIdsSchema.optional(),
    grantedChannelIds: GrantedChannelIdsSchema.optional(),
    expiresIn: z.enum(SPACE_INVITE_EXPIRY_OPTIONS).optional(),
    temporaryAccess: SpaceTemporaryAccessSchema.optional(),
});
export const CreateSpaceInviteLinkSchema = z.object({
    grantedRoleIds: GrantedRoleIdsSchema.optional(),
    grantedChannelIds: GrantedChannelIdsSchema.optional(),
    expiresIn: z.enum(SPACE_INVITE_EXPIRY_OPTIONS).optional(),
    temporaryAccess: SpaceTemporaryAccessSchema.optional(),
    maxUses: z.number().int().min(1).max(SPACE_INVITE_MAX_USES_LIMIT).nullable().optional(),
});
/** Path parameter shape for the public link endpoints. */
export const SpaceInviteCodeSchema = z.string().regex(SPACE_INVITE_CODE_PATTERN);
const ObjectIdHex24Schema = z.string().regex(/^[0-9a-f]{24}$/i);
const SpaceMessageCommonFields = {
    clientMessageId: z.guid(),
    replyToMessageId: z.string().length(24).optional(),
    /** Post into this message's thread (server resolves to the top-level root). Exclusive with `replyToMessageId`. */
    threadRootId: ObjectIdHex24Schema.optional(),
    /** With `threadRootId`: also show the reply in the channel stream. */
    alsoSendToChannel: z.boolean().optional(),
    /**
     * Explicit @user mentions only (not @here / role expansion). This coarse
     * bound just rejects pathological payloads; the authoritative per-message cap
     * (15 distinct users) is enforced server-side so 16–50 gets a clear error.
     */
    mentionedIdentityIds: z.array(z.string().length(24)).max(50).optional(),
    /** Role ids to ping; server expands to members after RBAC checks. */
    mentionedRoleIds: z.array(z.string().length(24)).max(20).optional(),
    /** When true, ping channel viewers; requires `mentionHere` permission. */
    mentionHere: z.boolean().optional(),
    expiresInSeconds: z
        .number()
        .int()
        .positive()
        .max(SPACE_MESSAGE_MAX_EXPIRES_SECONDS)
        .optional(),
    attachmentMediaIds: z
        .array(z.string().min(1).max(100))
        .max(SPACE_MESSAGE_MAX_ATTACHMENTS)
        .optional(),
    e2eMediaIds: z
        .array(z.string().min(1).max(100))
        .max(SPACE_MESSAGE_MAX_ATTACHMENTS)
        .optional(),
};
const SpaceMessageCipherFields = {
    ciphertext: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH),
    nonce: base64Field(500),
    cipherId: CipherIdSchema,
};
function spaceMessageBodyRefine(v) {
    const hasContent = !!v.content;
    const hasCipher = !!(v.ciphertext && v.nonce && v.cipherId);
    const hasClearAttachments = !!v.attachmentMediaIds?.length;
    const hasE2eAttachments = !!v.e2eMediaIds?.length;
    if (hasContent && hasCipher)
        return false;
    if (hasClearAttachments && hasE2eAttachments)
        return false;
    if (hasClearAttachments && hasCipher)
        return false;
    if (hasE2eAttachments && hasContent)
        return false;
    // Encrypted path requires the cipher triple and an Ed25519 signature.
    if (hasCipher || hasE2eAttachments)
        return hasCipher && !!v.signature;
    // Plaintext: text and/or cleartext attachments. Signatures are E2EE-only.
    if (v.signature)
        return false;
    return hasContent || hasClearAttachments;
}
export const SendSpaceMessageSchema = z
    .object({
    content: z.string().min(1).max(SPACE_MESSAGE_MAX_LENGTH).optional(),
    ...SpaceMessageCipherFields,
    ciphertext: SpaceMessageCipherFields.ciphertext.optional(),
    nonce: SpaceMessageCipherFields.nonce.optional(),
    cipherId: SpaceMessageCipherFields.cipherId.optional(),
    /** Ed25519 signature over Space message context (required with cipher fields). */
    signature: base64Field(8192).optional(),
    ...SpaceMessageCommonFields,
})
    .refine(spaceMessageBodyRefine, {
    message: 'Provide either content (+ optional attachmentMediaIds) or ciphertext+nonce+cipherId+signature (+ optional e2eMediaIds), not mixed',
});
export const EditSpaceMessageSchema = z
    .object({
    content: z.string().min(1).max(SPACE_MESSAGE_MAX_LENGTH).optional(),
    ciphertext: SpaceMessageCipherFields.ciphertext.optional(),
    nonce: SpaceMessageCipherFields.nonce.optional(),
    cipherId: SpaceMessageCipherFields.cipherId.optional(),
    signature: base64Field(8192).optional(),
    attachmentMediaIds: z
        .array(z.string().min(1).max(100))
        .max(SPACE_MESSAGE_MAX_ATTACHMENTS)
        .optional(),
    e2eMediaIds: z
        .array(z.string().min(1).max(100))
        .max(SPACE_MESSAGE_MAX_ATTACHMENTS)
        .optional(),
})
    .refine(spaceMessageBodyRefine, {
    message: 'Provide either content (+ optional attachmentMediaIds) or ciphertext+nonce+cipherId+signature (+ optional e2eMediaIds), not mixed',
});
const ObjectIdHexSchema = z.string().regex(/^[0-9a-f]{24}$/);
export const SpaceAnnouncementAudienceSchema = z
    .object({
    channelIds: z.array(ObjectIdHexSchema).max(SPACE_ANNOUNCEMENT_AUDIENCE_MAX_CHANNELS).default([]),
    roleIds: z.array(ObjectIdHexSchema).max(SPACE_ANNOUNCEMENT_AUDIENCE_MAX_ROLES).default([]),
    match: z.enum(SPACE_ANNOUNCEMENT_AUDIENCE_MATCHES).default('all'),
})
    .transform((v) => normalizeAnnouncementAudience(v));
const SpaceAnnouncementBodyFields = {
    content: z.string().min(1).max(SPACE_ANNOUNCEMENT_CONTENT_MAX_LENGTH).optional(),
    ciphertext: base64Field(SPACE_ANNOUNCEMENT_CIPHERTEXT_MAX_LENGTH).optional(),
    nonce: SpaceMessageCipherFields.nonce.optional(),
    cipherId: SpaceMessageCipherFields.cipherId.optional(),
    /** Ed25519 signature over the announcement context (required with cipher fields). */
    signature: base64Field(8192).optional(),
    attachmentMediaIds: z
        .array(z.string().min(1).max(100))
        .max(SPACE_ANNOUNCEMENT_MAX_ATTACHMENTS)
        .optional(),
    e2eMediaIds: z
        .array(z.string().min(1).max(100))
        .max(SPACE_ANNOUNCEMENT_MAX_ATTACHMENTS)
        .optional(),
    audience: SpaceAnnouncementAudienceSchema.optional(),
};
const SPACE_ANNOUNCEMENT_BODY_MESSAGE = 'Provide either content (+ optional attachmentMediaIds) or ciphertext+nonce+cipherId+signature (+ optional e2eMediaIds), not mixed';
export const CreateSpaceAnnouncementSchema = z
    .object({
    clientAnnouncementId: z.guid(),
    ...SpaceAnnouncementBodyFields,
})
    .refine(spaceMessageBodyRefine, { message: SPACE_ANNOUNCEMENT_BODY_MESSAGE });
export const EditSpaceAnnouncementSchema = z
    .object({
    /** The revision this edit produces (current + 1). */
    revision: z.number().int().min(2),
    ...SpaceAnnouncementBodyFields,
})
    .refine(spaceMessageBodyRefine, { message: SPACE_ANNOUNCEMENT_BODY_MESSAGE });
export const ListSpaceAnnouncementsQuery = z.object({
    /** Announcement id cursor: return items older than this one. */
    before: ObjectIdHexSchema.optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(SPACE_ANNOUNCEMENT_PAGE_MAX)
        .default(SPACE_ANNOUNCEMENT_PAGE_DEFAULT),
});
export const AddSpaceReactionSchema = z
    .object({
    emoji: z
        .string()
        .min(1)
        .max(REACTION_EMOJI_MAX_LENGTH)
        .refine(isValidReactionEmoji, {
        message: 'emoji must be a Unicode emoji or a custom emoji token',
    })
        .optional(),
    tag: z
        .string()
        .length(SPACE_REACTION_TAG_HEX_LENGTH)
        .regex(/^[0-9a-f]+$/, 'tag must be lowercase hex')
        .optional(),
    signature: base64Field(8192).optional(),
})
    .refine((v) => {
    const hasEmoji = v.emoji != null;
    const hasTagged = v.tag != null && v.signature != null;
    return ((hasEmoji && v.tag == null && v.signature == null) ||
        (hasTagged && v.emoji == null));
}, { message: 'Provide either emoji or tag+signature, not mixed' });
export const PinSpaceMessageSchema = z.object({
    messageId: z.string().length(24),
});
export const MarkSpaceChannelReadSchema = z.object({
    lastReadMessageId: z.string().length(24),
});
const SpacePermissionSchema = z.enum(SPACE_PERMISSIONS);
export const CreateSpaceRoleSchema = z
    .object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(SPACE_ROLE_DESCRIPTION_MAX_LENGTH).optional(),
    permissions: z.array(SpacePermissionSchema).max(SPACE_PERMISSIONS.length).optional(),
    color: z.union([z.string().regex(/^#[0-9a-fA-F]{6}$/), z.null()]).optional(),
    displaySeparately: z.boolean().optional(),
    mentionable: z.boolean().optional(),
    position: z.number().int().min(0).max(10_000).optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    cipherId: CipherIdSchema.optional(),
})
    .refine((v) => {
    const hasCipher = !!(v.encryptedName && v.nameNonce && v.cipherId);
    const hasPartial = !!(v.encryptedName || v.nameNonce || v.cipherId) && !hasCipher;
    return !hasPartial;
}, { message: 'encryptedName, nameNonce, and cipherId must be provided together' });
export const UpdateSpaceRoleSchema = z
    .object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(SPACE_ROLE_DESCRIPTION_MAX_LENGTH).optional(),
    permissions: z.array(SpacePermissionSchema).max(SPACE_PERMISSIONS.length).optional(),
    color: z.union([z.string().regex(/^#[0-9a-fA-F]{6}$/), z.null()]).optional(),
    displaySeparately: z.boolean().optional(),
    mentionable: z.boolean().optional(),
    isDefaultMember: z.boolean().optional(),
    position: z.number().int().min(0).max(10_000).optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    cipherId: CipherIdSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field is required',
})
    .refine((v) => {
    const hasCipher = !!(v.encryptedName && v.nameNonce && v.cipherId);
    const hasPartial = !!(v.encryptedName || v.nameNonce || v.cipherId) && !hasCipher;
    return !hasPartial;
}, { message: 'encryptedName, nameNonce, and cipherId must be provided together' });
export const SetMemberRolesSchema = z.object({
    roleIds: z.array(z.string().length(24)).max(50),
});
export const ReorderSpaceRolesSchema = z.object({
    roleIds: z.array(z.string().length(24)).max(200),
});
export const BanSpaceMemberSchema = z.object({
    reason: z.string().trim().min(1).max(500),
    duration: z.enum(['1h', '1d', '7d', '30d', 'permanent']),
});
/** Patch Space-scoped nickname / colour for a member. Null clears a field. */
export const UpdateSpaceMemberProfileSchema = z
    .object({
    nickname: z.union([z.string().max(50), z.null()]).optional(),
    color: z.union([z.string().regex(/^#[0-9a-fA-F]{6}$/), z.null()]).optional(),
})
    .refine((v) => v.nickname !== undefined || v.color !== undefined, {
    message: 'At least one of nickname or color is required',
});
export const CreateSpaceChannelSchema = z
    .object({
    name: z.string().min(SPACE_CHANNEL_NAME_MIN_LENGTH).max(SPACE_CHANNEL_NAME_MAX_LENGTH).optional(),
    type: z.enum(SPACE_CHANNEL_TYPES),
    allowedRoleIds: z.array(z.string().length(24)).max(50).optional(),
    categoryId: z.string().length(24).optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    cipherId: CipherIdSchema.optional(),
    /**
     * When true (or omitted on an e2ee Space), the channel inherits the parent
     * Space's `cipherCheck` unless an explicit `cipherCheck` is provided.
     * When false on a non-e2ee Space, the channel stores no `cipherCheck`.
     * Encrypted Spaces reject `encrypt: false`.
     */
    encrypt: z.boolean().optional(),
    cipherCheck: CipherCheckSchema.optional(),
    inheritAllowedRoleIds: z.boolean().optional(),
    inheritCipherCheck: z.boolean().optional(),
})
    .refine((v) => {
    const hasPlain = typeof v.name === 'string' && v.name.length > 0;
    const hasCipher = !!(v.encryptedName && v.nameNonce && v.cipherId);
    return (hasPlain || hasCipher) && !(hasPlain && hasCipher);
}, { message: 'Provide either name (plaintext) or encryptedName+nameNonce+cipherId, not both' })
    .refine((v) => !(v.encrypt === false && v.cipherCheck), {
    message: 'cipherCheck cannot be set when encrypt is false',
    path: ['cipherCheck'],
});
export const UpdateSpaceChannelSchema = z
    .object({
    name: z.string().min(SPACE_CHANNEL_NAME_MIN_LENGTH).max(SPACE_CHANNEL_NAME_MAX_LENGTH).optional(),
    allowedRoleIds: z.array(z.string().length(24)).max(50).optional(),
    categoryId: z.string().length(24).nullable().optional(),
    position: z.number().int().min(0).max(10_000).optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    cipherId: CipherIdSchema.optional(),
    /** Set/clear channel content encryption (`cipherCheck`). */
    encrypt: z.boolean().optional(),
    cipherCheck: CipherCheckSchema.optional(),
    /**
     * Unpublished plaintext of the *current* stored challenge. Required to
     * replace or clear an existing `cipherCheck`. Distinct from
     * `cipherCheck.knownValue` (the new challenge being stored).
     */
    currentKnownValue: KnownValueSchema.optional(),
    inheritAllowedRoleIds: z.boolean().optional(),
    inheritCipherCheck: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field is required',
})
    .refine((v) => {
    const hasPlain = typeof v.name === 'string' && v.name.length > 0;
    const hasCipher = !!(v.encryptedName && v.nameNonce && v.cipherId);
    const hasPartial = !!(v.encryptedName || v.nameNonce || v.cipherId) && !hasCipher;
    if (hasPartial)
        return false;
    if (hasPlain && hasCipher)
        return false;
    return true;
}, { message: 'Provide either name (plaintext) or encryptedName+nameNonce+cipherId, not both' })
    .refine((v) => !(v.encrypt === false && v.cipherCheck), {
    message: 'cipherCheck cannot be set when encrypt is false',
    path: ['cipherCheck'],
});
const spaceCategoryNameFields = {
    name: z.string().min(SPACE_CHANNEL_NAME_MIN_LENGTH).max(SPACE_CHANNEL_NAME_MAX_LENGTH).optional(),
    allowedRoleIds: z.array(z.string().length(24)).max(50).optional(),
    encryptedName: base64Field(SPACE_MESSAGE_CIPHERTEXT_MAX_LENGTH).optional(),
    nameNonce: base64Field(500).optional(),
    cipherId: CipherIdSchema.optional(),
    parentCategoryId: z.string().length(24).nullable().optional(),
    /**
     * Default content Cipher for channels created in this category.
     * When true (or omitted on an e2ee Space / under an encrypted parent),
     * inherits parent/Space `cipherCheck` unless an explicit one is provided.
     */
    encrypt: z.boolean().optional(),
    cipherCheck: CipherCheckSchema.optional(),
    inheritAllowedRoleIds: z.boolean().optional(),
    inheritCipherCheck: z.boolean().optional(),
    forceChildrenAcl: z.boolean().optional(),
    forceChildrenCipher: z.boolean().optional(),
};
export const CreateSpaceChannelCategorySchema = z
    .object(spaceCategoryNameFields)
    .refine((v) => {
    const hasPlain = typeof v.name === 'string' && v.name.length > 0;
    const hasCipher = !!(v.encryptedName && v.nameNonce && v.cipherId);
    return (hasPlain || hasCipher) && !(hasPlain && hasCipher);
}, { message: 'Provide either name (plaintext) or encryptedName+nameNonce+cipherId, not both' })
    .refine((v) => !(v.encrypt === false && v.cipherCheck), {
    message: 'cipherCheck cannot be set when encrypt is false',
    path: ['cipherCheck'],
});
export const UpdateSpaceChannelCategorySchema = z
    .object({
    ...spaceCategoryNameFields,
    position: z.number().int().min(0).max(10_000).optional(),
    /**
     * Unpublished plaintext of the *current* stored challenge. Required to
     * replace or clear an existing category `cipherCheck`.
     */
    currentKnownValue: KnownValueSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field is required',
})
    .refine((v) => {
    const hasPlain = typeof v.name === 'string' && v.name.length > 0;
    const hasCipher = !!(v.encryptedName && v.nameNonce && v.cipherId);
    const hasPartial = !!(v.encryptedName || v.nameNonce || v.cipherId) && !hasCipher;
    if (hasPartial)
        return false;
    if (hasPlain && hasCipher)
        return false;
    return true;
}, { message: 'Provide either name (plaintext) or encryptedName+nameNonce+cipherId, not both' })
    .refine((v) => !(v.encrypt === false && v.cipherCheck), {
    message: 'cipherCheck cannot be set when encrypt is false',
    path: ['cipherCheck'],
});
export const UpdateSpaceChannelLayoutSchema = z.object({
    groups: z
        .array(z.object({
        parentCategoryId: z.string().length(24).nullable(),
        items: z
            .array(z.discriminatedUnion('type', [
            z.object({ type: z.literal('channel'), id: z.string().length(24) }),
            z.object({ type: z.literal('category'), id: z.string().length(24) }),
        ]))
            .max(500),
    }))
        .max(201),
});
/** Full-replace role permission overrides for a channel or category node. */
export const SetSpaceRolePermissionOverridesSchema = z.object({
    overrides: z
        .array(z.object({
        roleId: z.string().length(24),
        permissions: z.array(z.enum(SPACE_PERMISSIONS)).max(SPACE_PERMISSIONS.length),
    }))
        .max(50),
});
export const SpaceNotifyLevelSchema = z.enum(SPACE_NOTIFY_LEVELS);
const notifyOverrideMap = z
    .record(ObjectIdHexSchema, SpaceNotifyLevelSchema.nullable())
    .refine((m) => Object.keys(m).length <= SPACE_NOTIFICATION_OVERRIDES_MAX_ENTRIES, {
    message: `At most ${SPACE_NOTIFICATION_OVERRIDES_MAX_ENTRIES} entries`,
});
/** `null` clears an override so it inherits from the parent again. */
export const SpaceNotificationOverridesPatchSchema = z
    .object({
    level: SpaceNotifyLevelSchema.nullable().optional(),
    announcements: z.boolean().nullable().optional(),
    spaceChanges: z.boolean().nullable().optional(),
    categories: notifyOverrideMap.optional(),
    channels: notifyOverrideMap.optional(),
})
    .strict();
export const SpaceNotificationDefaultsSchema = z
    .object({
    level: SpaceNotifyLevelSchema,
    announcements: z.boolean(),
    spaceChanges: z.boolean(),
})
    .strict();
export const NotificationDisplaySettingsSchema = z
    .object({
    conversations: z
        .object({ showSender: z.boolean(), showConversationName: z.boolean() })
        .strict(),
    spaces: z
        .object({ showSender: z.boolean(), showChannel: z.boolean(), showSpace: z.boolean() })
        .strict(),
})
    .strict();
export const SpaceActivityPreferencesSchema = z
    .object({
    sort: z.enum(SPACE_ACTIVITY_SORTS).optional(),
    topWindow: z.enum(SPACE_ACTIVITY_TOP_WINDOWS).optional(),
    include: z
        .object({
        announcements: z.boolean().optional(),
        pins: z.boolean().optional(),
        posts: z.boolean().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const UpdateSpacePreferencesSchema = z.object({
    favorited: z.boolean().optional(),
    lurking: z.boolean().optional(),
    /** `null` clears the saved choice. */
    useSpaceTheme: z.boolean().nullable().optional(),
    notifications: SpaceNotificationOverridesPatchSchema.optional(),
    /** Replaces the saved feed controls; `null` clears them. */
    activity: SpaceActivityPreferencesSchema.nullable().optional(),
});
const activityWeight = z.number().int().min(0).max(SPACE_ACTIVITY_WEIGHT_MAX);
export const UpdateSpaceActivitySettingsSchema = z
    .object({
    defaultSort: z.enum(SPACE_ACTIVITY_SORTS),
    defaultTopWindow: z.enum(SPACE_ACTIVITY_TOP_WINDOWS),
    weights: z
        .object({
        reaction: activityWeight,
        inlineReply: activityWeight,
        threadReply: activityWeight,
        ephemeralReply: activityWeight,
        pinBonus: activityWeight,
        announcement: activityWeight,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=spaces.js.map